import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, Crown, Sparkles, Check, Phone, MessageSquare,
  MapPin, Users, Clock, CalendarDays, Loader2, ArrowRightLeft, ArrowRight, Repeat,
  CreditCard, DollarSign,
} from "lucide-react";
import BUSINESS from "../config/business";
import MobileBottomSheet, { SheetField, sheetInputCls, GoldButton } from "./MobileBottomSheet";
import GoldParticles from "./GoldParticles";
import { haptic, HAPTIC } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** MobileChauffeur — /m/chauffeur */
export default function MobileChauffeur() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const prefillFromHome = location.state?.prefill;

  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(params.get("book") === "1" || !!prefillFromHome);
  const [preset, setPreset] = useState(prefillFromHome || null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vehicles`, { params: { available_for: "chauffeur" } });
        if (!cancelled) setVehicles(data || []);
      } catch (e) {
        if (!cancelled) setVehicles([]);
      } finally {
        if (!cancelled) setLoadingVehicles(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // If a prefill was passed via navigate("/m/chauffeur", { state:{ prefill } }),
  // apply it once vehicles have loaded, then clear the router state.
  useEffect(() => {
    if (prefillFromHome) {
      setSheetOpen(true);
      setPreset(prefillFromHome);
      // Clear state so refreshing doesn't re-open the sheet.
      window.history.replaceState({}, document.title);
    }
  }, [prefillFromHome]);

  const openBookingWith = (vehicle) => {
    setPreset(vehicle ? { vehicle_id: vehicle.id } : null);
    haptic(HAPTIC.select);
    setSheetOpen(true);
  };

  return (
    <div data-testid="mobile-chauffeur" className="min-h-screen bg-[#071A2F] text-white pb-28 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute -top-32 -right-16 w-80 h-80 rounded-full bg-[#39D6FF]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 -left-24 w-96 h-96 rounded-full bg-[#16B3DD]/8 blur-3xl pointer-events-none" />

      {/* Top bar */}
      <div className="flex items-center justify-between px-5 pt-6">
        <button
          type="button"
          onClick={() => navigate("/m")}
          data-testid="chauffeur-back"
          className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white active:scale-95 transition"
          aria-label="Back"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="text-[9px] tracking-[0.4em] text-[#39D6FF]/70">CHAUFFEUR</div>
        <div className="w-10 h-10" />
      </div>

      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="px-6 pt-6 pb-8"
      >
        <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full border border-[#39D6FF]/40 bg-[#39D6FF]/10 text-[10px] tracking-[0.3em] text-[#39D6FF]">
          <Crown size={11} /> LUXURY GROUND SERVICE
        </div>
        <h1 className="font-serif text-4xl leading-tight">
          Arrive in <span className="text-transparent bg-clip-text bg-gradient-to-b from-[#F8FAFC] to-[#39D6FF]">effortless</span> luxury.
        </h1>
        <p className="mt-3 text-sm text-white/60">
          A private chauffeur, on your schedule — {BUSINESS.serviceAreas.join(" · ")}.
        </p>
        <div className="mt-6 flex gap-3">
          <GoldButton
            testId="chauffeur-book-cta"
            onClick={() => openBookingWith(null)}
          >
            BOOK A RIDE
          </GoldButton>
        </div>
        <div className="mt-3 flex gap-3">
          <a
            href={`tel:${BUSINESS.phoneTel}`}
            data-testid="chauffeur-call"
            className="flex-1 py-3 rounded-full text-center text-[11px] tracking-[0.28em] text-white/80 border border-white/10 bg-white/5 hover:bg-white/8 active:scale-[0.98] transition inline-flex items-center justify-center gap-2"
          >
            <Phone size={13} /> CALL CONCIERGE
          </a>
          <a
            href={`sms:${BUSINESS.phoneTel}`}
            data-testid="chauffeur-text"
            className="flex-1 py-3 rounded-full text-center text-[11px] tracking-[0.28em] text-white/80 border border-white/10 bg-white/5 hover:bg-white/8 active:scale-[0.98] transition inline-flex items-center justify-center gap-2"
          >
            <MessageSquare size={13} /> TEXT US
          </a>
        </div>
      </motion.section>

      {/* Service types */}
      <section className="px-6 mt-2">
        <SectionHeading label="SERVICES" title="Every occasion, elevated" />
        <div className="mt-4 grid grid-cols-2 gap-3">
          {BUSINESS.chauffeur.serviceTypes.map((s, i) => (
            <motion.button
              key={s}
              type="button"
              data-testid={`svc-${s.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => {
                haptic(HAPTIC.tap);
                setPreset({ trip_purpose: s });
                setSheetOpen(true);
              }}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.06 * i }}
              className="text-left rounded-2xl p-4 border border-white/10 hover:border-[#39D6FF]/40 bg-white/[0.04] active:scale-[0.98] transition"
            >
              <div className="w-8 h-8 rounded-full bg-[#39D6FF]/10 border border-[#39D6FF]/30 flex items-center justify-center mb-2 text-[#39D6FF]">
                <Sparkles size={14} />
              </div>
              <div className="text-xs font-medium text-white">{s}</div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Fleet */}
      <section className="mt-10">
        <div className="px-6">
          <SectionHeading label="FLEET" title="Signature vehicles" />
        </div>
        <div className="mt-4 overflow-x-auto no-scrollbar">
          <div className="flex gap-4 px-6 pb-2 min-w-max">
            {loadingVehicles ? (
              <div className="flex items-center gap-2 text-white/40 text-xs py-8">
                <Loader2 size={14} className="animate-spin" /> Loading fleet…
              </div>
            ) : vehicles.length === 0 ? (
              <div className="text-white/40 text-xs py-8">No chauffeur vehicles available.</div>
            ) : (
              vehicles.map((v, i) => (
                <FleetCard key={v.id} vehicle={v} delay={0.05 * i} onBook={() => openBookingWith(v)} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Trust line */}
      <div className="mt-10 mx-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-center">
        <div className="text-[9px] tracking-[0.4em] text-[#39D6FF]/70 mb-2">WHY PLENTY RYDEZ</div>
        <div className="grid grid-cols-3 gap-3 mt-2">
          {[
            { t: "Vetted", s: "Chauffeurs" },
            { t: "24/7", s: "Concierge" },
            { t: "Flat", s: "Pricing" },
          ].map((x) => (
            <div key={x.t} className="">
              <div className="font-serif text-lg text-[#39D6FF]">{x.t}</div>
              <div className="text-[10px] text-white/50 tracking-widest">{x.s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Booking sheet */}
      <ChauffeurBookingSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        vehicles={vehicles}
        preset={preset}
      />
    </div>
  );
}

function SectionHeading({ label, title }) {
  return (
    <>
      <div className="text-[9px] tracking-[0.4em] text-[#39D6FF]/70">{label}</div>
      <h2 className="mt-1 font-serif text-2xl">{title}</h2>
    </>
  );
}

function FleetCard({ vehicle, delay, onBook }) {
  const img = vehicle.image_url || (vehicle.images && vehicle.images[0]);
  return (
    <motion.div
      data-testid={`fleet-card-${vehicle.id}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      className="w-64 shrink-0 rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02]"
    >
      <div className="relative aspect-[4/3] bg-[#071A2F] overflow-hidden">
        {img ? (
          <img src={img} alt={vehicle.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">No image</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute bottom-3 left-3 right-3">
          <div className="text-[9px] tracking-[0.35em] text-[#39D6FF]">{vehicle.category?.toUpperCase()}</div>
          <div className="font-serif text-lg leading-tight">{vehicle.name}</div>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between text-[11px] text-white/60 mb-3">
          <span className="inline-flex items-center gap-1"><Users size={11} /> {vehicle.capacity}</span>
          <span className="text-[#39D6FF]">${Number(vehicle.hourly_rate).toFixed(0)}/hr</span>
        </div>
        <button
          type="button"
          onClick={onBook}
          data-testid={`fleet-book-${vehicle.id}`}
          className="w-full py-2.5 rounded-full text-[10px] tracking-[0.3em] font-semibold text-black active:scale-[0.98] transition"
          style={{
            background: "linear-gradient(180deg, #F8FAFC 0%, #39D6FF 60%, #39D6FF 100%)",
            boxShadow: "0 4px 18px -4px rgba(57,214,255,0.55)",
          }}
        >
          BOOK THIS
        </button>
      </div>
    </motion.div>
  );
}

function ChauffeurBookingSheet({ open, onClose, vehicles, preset }) {
  const [state, setState] = useState(() => defaultRideState());
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setConfirmed(null);
      setError("");
      setState({ ...defaultRideState(), ...(preset || {}) });
    }
  }, [open, preset]);

  const selectedVehicle = useMemo(
    () => vehicles.find(v => v.id === state.vehicle_id) || null,
    [vehicles, state.vehicle_id]
  );

  const tripType = state.trip_type || "hourly";
  const isHourly = tripType === "hourly";
  const vehicleMinH = Math.max(selectedVehicle?.min_hours || 3, 1);
  // Only hourly enforces the vehicle's min-hours. One-way & round-trip allow shorter trips (e.g. airport drops).
  const minHours = isHourly ? vehicleMinH : (tripType === "round_trip" ? 2 : 1);

  const canSubmit = state.full_name && state.email && state.phone
    && state.pickup && state.dropoff && state.pickup_datetime && state.vehicle_id
    && Number(state.estimated_duration_hours) >= minHours;

  const estimate = useMemo(() => {
    if (!selectedVehicle) return null;
    const hrs = Math.max(Number(state.estimated_duration_hours || 0), minHours);
    return { hrs, total: 25 + Number(selectedVehicle.hourly_rate) * hrs };
  }, [selectedVehicle, state.estimated_duration_hours, minHours]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    haptic(HAPTIC.tap);
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        vehicle_id: state.vehicle_id,
        pickup: state.pickup,
        dropoff: state.dropoff,
        pickup_datetime: new Date(state.pickup_datetime).toISOString(),
        return_datetime: tripType === "round_trip" && state.return_datetime
          ? new Date(state.return_datetime).toISOString() : null,
        trip_type: tripType,
        payment_method: state.payment_method || "card",
        passenger_count: Number(state.passenger_count || 1),
        special_requests: state.special_requests || "",
        estimated_duration_hours: Number(state.estimated_duration_hours || minHours),
        trip_purpose: state.trip_purpose || "other",
        full_name: state.full_name,
        email: state.email,
        phone: state.phone,
      };
      const { data } = await axios.post(`${API}/bookings/ride`, payload);
      haptic(HAPTIC.success);
      setConfirmed(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileBottomSheet
      open={open}
      onClose={onClose}
      title={confirmed ? "Request received" : "Book your ride"}
      subtitle={confirmed ? "AWAITING APPROVAL" : "CHAUFFEUR SERVICE"}
      testId="chauffeur-sheet"
    >
      {confirmed ? (
        <ConfirmationPanel booking={confirmed} onDone={onClose} />
      ) : (
        <form onSubmit={submit} data-testid="chauffeur-form" className="pt-1">
          {/* Trip type — hourly / one-way / round-trip */}
          <div className="mb-5">
            <div className="text-[10px] tracking-[0.35em] text-white/50 mb-2 uppercase">Trip type</div>
            <div data-testid="field-trip-type" className="grid grid-cols-3 gap-2">
              {[
                { k: "one_way",   label: "One-way",   sub: "Airport drop",  Icon: ArrowRight },
                { k: "round_trip",label: "Round-trip",sub: "Out & return", Icon: Repeat },
                { k: "hourly",    label: "Hourly",    sub: `${vehicleMinH}h min`, Icon: Clock },
              ].map(({ k, label, sub, Icon }) => {
                const active = tripType === k;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`trip-type-${k}`}
                    onClick={() => {
                      haptic(HAPTIC.select);
                      setState((s) => {
                        const nextHours = k === "one_way"
                          ? Math.max(1, Number(s.estimated_duration_hours) || 1.5)
                          : Math.max(vehicleMinH, Number(s.estimated_duration_hours) || vehicleMinH);
                        return {
                          ...s,
                          trip_type: k,
                          estimated_duration_hours: k === "one_way"
                            ? (Number(s.estimated_duration_hours) < 1 ? 1.5 : Number(s.estimated_duration_hours))
                            : nextHours,
                        };
                      });
                    }}
                    className={`rounded-2xl p-3 border text-left transition ${
                      active
                        ? "border-[#39D6FF] bg-[#39D6FF]/12"
                        : "border-white/10 bg-white/5 hover:border-white/25"
                    }`}
                  >
                    <Icon size={14} className={active ? "text-[#39D6FF]" : "text-white/50"} />
                    <div className={`mt-1 text-[11px] font-medium ${active ? "text-white" : "text-white/80"}`}>{label}</div>
                    <div className="text-[9px] tracking-widest text-white/40">{sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <SheetField label="Vehicle" testId="field-vehicle">
            <select
              className={sheetInputCls}
              value={state.vehicle_id}
              onChange={(e) => setState({ ...state, vehicle_id: e.target.value })}
              data-testid="input-vehicle"
              required
            >
              <option value="">Select a vehicle</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} · ${Number(v.hourly_rate).toFixed(0)}/hr · {v.capacity} pax
                </option>
              ))}
            </select>
          </SheetField>

          <SheetField label="Service type" testId="field-service-type">
            <select
              className={sheetInputCls}
              value={state.trip_purpose}
              onChange={(e) => setState({ ...state, trip_purpose: e.target.value })}
              data-testid="input-service-type"
            >
              {BUSINESS.chauffeur.serviceTypes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </SheetField>

          <div className="grid grid-cols-1 gap-0">
            <SheetField label="Pickup" testId="field-pickup">
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39D6FF]" />
                <input
                  className={`${sheetInputCls} pl-9`}
                  placeholder="Pickup address"
                  value={state.pickup}
                  onChange={(e) => setState({ ...state, pickup: e.target.value })}
                  data-testid="input-pickup"
                  required
                />
              </div>
            </SheetField>
            <SheetField label="Dropoff" testId="field-dropoff">
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39D6FF]" />
                <input
                  className={`${sheetInputCls} pl-9`}
                  placeholder="Dropoff address"
                  value={state.dropoff}
                  onChange={(e) => setState({ ...state, dropoff: e.target.value })}
                  data-testid="input-dropoff"
                  required
                />
              </div>
            </SheetField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <SheetField label="Pickup date & time" testId="field-datetime">
              <div className="relative">
                <CalendarDays size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39D6FF]" />
                <input
                  type="datetime-local"
                  className={`${sheetInputCls} pl-9`}
                  value={state.pickup_datetime}
                  onChange={(e) => setState({ ...state, pickup_datetime: e.target.value })}
                  data-testid="input-datetime"
                  required
                />
              </div>
            </SheetField>
            <SheetField label={isHourly ? `Hours (min ${minHours})` : "Est. hours"} testId="field-hours">
              <div className="relative">
                <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39D6FF]" />
                <input
                  type="number"
                  min={minHours}
                  step="0.5"
                  className={`${sheetInputCls} pl-9`}
                  value={state.estimated_duration_hours}
                  onChange={(e) => setState({ ...state, estimated_duration_hours: e.target.value })}
                  data-testid="input-hours"
                  required
                />
              </div>
            </SheetField>
          </div>

          {tripType === "round_trip" && (
            <SheetField label="Return date & time (optional)" testId="field-return-datetime">
              <div className="relative">
                <ArrowRightLeft size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39D6FF]" />
                <input
                  type="datetime-local"
                  className={`${sheetInputCls} pl-9`}
                  value={state.return_datetime}
                  onChange={(e) => setState({ ...state, return_datetime: e.target.value })}
                  data-testid="input-return-datetime"
                />
              </div>
            </SheetField>
          )}

          <SheetField label="Passengers" testId="field-passengers">
            <div className="relative">
              <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#39D6FF]" />
              <input
                type="number"
                min={1}
                max={20}
                className={`${sheetInputCls} pl-9`}
                value={state.passenger_count}
                onChange={(e) => setState({ ...state, passenger_count: e.target.value })}
                data-testid="input-passengers"
              />
            </div>
          </SheetField>

          <div className="border-t border-white/5 pt-4 mt-2">
            <div className="text-[9px] tracking-[0.4em] text-[#39D6FF]/70 mb-3">YOUR DETAILS</div>
            <SheetField label="Full name" testId="field-name">
              <input
                className={sheetInputCls}
                value={state.full_name}
                onChange={(e) => setState({ ...state, full_name: e.target.value })}
                data-testid="input-name"
                required
              />
            </SheetField>
            <div className="grid grid-cols-2 gap-3">
              <SheetField label="Email" testId="field-email">
                <input
                  type="email"
                  className={sheetInputCls}
                  value={state.email}
                  onChange={(e) => setState({ ...state, email: e.target.value })}
                  data-testid="input-email"
                  required
                />
              </SheetField>
              <SheetField label="Phone" testId="field-phone">
                <input
                  type="tel"
                  className={sheetInputCls}
                  value={state.phone}
                  onChange={(e) => setState({ ...state, phone: e.target.value })}
                  data-testid="input-phone"
                  required
                />
              </SheetField>
            </div>
          </div>

          <SheetField label="Special requests (optional)" testId="field-notes">
            <textarea
              rows={2}
              className={sheetInputCls}
              placeholder="Child seat, tint preferences, meet-and-greet, etc."
              value={state.special_requests}
              onChange={(e) => setState({ ...state, special_requests: e.target.value })}
              data-testid="input-notes"
            />
          </SheetField>

          {/* Payment method */}
          <div className="mb-5">
            <div className="text-[10px] tracking-[0.35em] text-white/50 mb-2 uppercase">Payment method</div>
            <div data-testid="field-payment-method" className="grid grid-cols-2 gap-2">
              {[
                { k: "card", label: "Card / Deposit", sub: "Secure link on approval", Icon: CreditCard },
                { k: "cash", label: "Cash at pickup",  sub: "No deposit — pay driver", Icon: DollarSign },
              ].map(({ k, label, sub, Icon }) => {
                const active = (state.payment_method || "card") === k;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`payment-${k}`}
                    onClick={() => { haptic(HAPTIC.select); setState({ ...state, payment_method: k }); }}
                    className={`rounded-2xl p-3 border text-left transition ${
                      active
                        ? "border-[#39D6FF] bg-[#39D6FF]/12"
                        : "border-white/10 bg-white/5 hover:border-white/25"
                    }`}
                  >
                    <Icon size={14} className={active ? "text-[#39D6FF]" : "text-white/50"} />
                    <div className={`mt-1 text-[11px] font-medium ${active ? "text-white" : "text-white/80"}`}>{label}</div>
                    <div className="text-[9px] tracking-widest text-white/40">{sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {estimate !== null && (
            <div className="mb-2 p-4 rounded-xl border border-[#39D6FF]/30 bg-[#39D6FF]/5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[9px] tracking-[0.35em] text-[#39D6FF]/70">ESTIMATE</div>
                  <div className="text-[11px] text-white/50 mt-0.5">
                    ${Number(selectedVehicle.hourly_rate).toFixed(0)}/hr · {estimate.hrs}h + $25 base
                  </div>
                </div>
                <div className="font-serif text-2xl text-[#39D6FF]">${estimate.total.toFixed(0)}</div>
              </div>
              <div className="mt-2 pt-2 border-t border-[#39D6FF]/15 flex items-center justify-between text-[11px]">
                {state.payment_method === "cash" ? (
                  <>
                    <span className="text-white/60">Due at pickup (cash)</span>
                    <span className="text-[#39D6FF] font-medium">${estimate.total.toFixed(2)}</span>
                  </>
                ) : (
                  <>
                    <span className="text-white/60">Deposit to reserve (50%)</span>
                    <span className="text-[#39D6FF] font-medium">${(estimate.total * 0.5).toFixed(2)}</span>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="mb-4 rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2 text-[10px] text-white/50 leading-relaxed">
            <span className="text-[#39D6FF]">Approval required.</span> Every reservation is reviewed by our team.
            You&apos;ll get a thank-you now, then a final confirmation
            {state.payment_method === "cash" ? " once approved (no online payment needed)." : " with a secure payment link once approved."}
          </div>

          {error && (
            <div data-testid="chauffeur-error" className="mb-3 text-[12px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <GoldButton type="submit" disabled={!canSubmit} loading={submitting} testId="chauffeur-submit">
            {submitting ? "SUBMITTING…" : "REQUEST RESERVATION"}
          </GoldButton>
          <p className="mt-3 text-[10px] text-white/40 text-center leading-relaxed">
            No card required now — management approves within the hour during business hours.
          </p>
        </form>
      )}
    </MobileBottomSheet>
  );
}

function ConfirmationPanel({ booking, onDone }) {
  return (
    <div data-testid="chauffeur-confirmation" className="pt-2 pb-8 relative">
      <div className="relative mx-auto w-20 h-20 mb-5">
        <GoldParticles count={20} spread={140} size={5} />
        <div className="relative w-20 h-20 rounded-full flex items-center justify-center"
          style={{
            background: "radial-gradient(circle, rgba(57,214,255,0.4), rgba(57,214,255,0.05))",
            border: "1px solid rgba(57,214,255,0.55)",
            boxShadow: "0 0 40px rgba(57,214,255,0.35)",
          }}
        >
          <Check size={30} className="text-[#39D6FF]" />
        </div>
      </div>
      <div className="text-center text-[9px] tracking-[0.4em] text-[#39D6FF]/70 mb-1">AWAITING APPROVAL</div>
      <h3 className="text-center font-serif text-2xl mb-2">Thank you — request received</h3>
      <p className="text-center text-xs text-white/60 mb-6 px-2">
        Our management team is reviewing your reservation right now. You&apos;ll receive a final
        confirmation email with a secure deposit link as soon as it&apos;s approved (usually within the hour).
      </p>
      <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 mb-4">
        <Row label="Booking ID" value={booking.id} mono />
        <Row label="Vehicle" value={booking.vehicle_name} />
        <Row label="Trip" value={tripTypeLabel(booking.trip_type)} />
        <Row label="Pickup" value={booking.pickup} />
        <Row label="Dropoff" value={booking.dropoff} />
        <Row label="Estimated total" value={`$${Number(booking.amount).toFixed(2)}`} />
        <Row label="Deposit (50%)" value={`$${Number(booking.deposit_amount || booking.amount * 0.5).toFixed(2)}`} gold />
      </div>
      <div className="rounded-xl border border-[#39D6FF]/25 bg-[#39D6FF]/[0.06] px-3 py-2 mb-6 text-center text-[10px] text-white/60 leading-relaxed">
        You&apos;ll see this booking marked as <span className="text-[#39D6FF]">Pending Approval</span> until our team confirms.
      </div>
      <div className="flex gap-3">
        <a
          href={`tel:${BUSINESS.phoneTel}`}
          data-testid="confirm-call"
          className="flex-1 py-3 rounded-full text-center text-[11px] tracking-[0.28em] text-white/80 border border-white/10 bg-white/5 hover:bg-white/8 transition inline-flex items-center justify-center gap-2"
        >
          <Phone size={13} /> CALL
        </a>
        <button
          type="button"
          onClick={onDone}
          data-testid="confirm-done"
          className="flex-1 py-3 rounded-full text-center text-[11px] tracking-[0.28em] font-semibold text-black active:scale-[0.98] transition"
          style={{
            background: "linear-gradient(180deg, #F8FAFC 0%, #39D6FF 50%, #39D6FF 100%)",
            boxShadow: "0 4px 18px -4px rgba(57,214,255,0.55)",
          }}
        >
          DONE
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, mono, gold }) {
  return (
    <div className="flex items-start justify-between py-2 border-b last:border-b-0 border-white/5">
      <span className="text-[10px] tracking-[0.28em] text-white/45 pt-0.5">{label.toUpperCase()}</span>
      <span className={`text-xs text-right max-w-[62%] ${mono ? "font-mono" : ""} ${gold ? "text-[#39D6FF] font-medium" : "text-white/90"}`}>
        {value}
      </span>
    </div>
  );
}

function tripTypeLabel(t) {
  return t === "one_way" ? "One-way" : t === "round_trip" ? "Round-trip" : "Hourly";
}

function defaultRideState() {
  return {
    vehicle_id: "",
    trip_type: "hourly",
    payment_method: "card",
    trip_purpose: BUSINESS.chauffeur.serviceTypes[0],
    pickup: "",
    dropoff: "",
    pickup_datetime: "",
    return_datetime: "",
    estimated_duration_hours: 3,
    passenger_count: 1,
    special_requests: "",
    full_name: "",
    email: "",
    phone: "",
    sms_consent: { transactional_opt_in: false, marketing_opt_in: false, marketing_opt_out: false },
  };
}
